import React, { useState, useEffect } from 'react';
import '../../styles/Auth.css';
import apiService from '../../apiService';


const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin, showToast }) => {
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'reset' | 'success'
  const [formData, setFormData] = useState({
    email: '',
    otp: ['', '', '', '', '', ''],
    password: '',
    password_confirmation: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [timer, setTimer] = useState(600);
  const [canResend, setCanResend] = useState(false);

  // Disable scrolling and interaction with background when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Disable scrolling on body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Re-enable scrolling
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Prevent clicks on backdrop from closing (only close button should work)
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    return strength;
  };

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value.trim()) return 'Please enter your email address';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address';
        }
        return '';

      case 'password':
        if (!value) return 'Please enter a password';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/(?=.*[a-z])(?=.*[A-Z])/.test(value)) {
          return 'Password must contain both uppercase and lowercase letters';
        }
        if (!/(?=.*\d)/.test(value)) {
          return 'Password must contain at least one number';
        }
        return '';

      case 'password_confirmation':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';

      default:
        return '';
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calculate password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Clear error when user starts typing
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...formData.otp];
      newOtp[index] = value;
      setFormData(prev => ({ ...prev, otp: newOtp }));
      
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  // Handle blur
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Send the password-reset OTP to the user's email (always hits the backend)
  const handleSendOTP = async (e) => {
    e.preventDefault();

    const error = validateField('email', formData.email);
    if (error) {
      setErrors({ email: error });
      setTouched({ email: true });
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiService.forgotPassword(formData.email);

      if (!response.success) {
        const msg = response?.data?.errors?.email?.[0]
          || response?.data?.message
          || 'No account found with this email address';
        setErrors({ email: msg });
        return;
      }

      setStep('otp');
      startTimer();
      showToast('OTP sent to your email. Please check your inbox.', 'success');
    } catch (error) {
      console.error('Error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP (localStorage version)
  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    const otpValue = formData.otp.join('');
    if (otpValue.length !== 6) {
      setErrors({ otp: 'Please enter complete OTP' });
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

    //  const resetSession = JSON.parse(sessionStorage.getItem('passwordReset') || '{}');
      
    //  if (otpValue === resetSession.otp || otpValue === '123456') {
      const response = await apiService.verifyOtp(formData.email, otpValue);
      if (response.success) {
        setStep('reset');
        setErrors({});
      } else {
        setErrors({ otp: response?.data?.message || 'Invalid OTP. Please try again.' });
      }
    } catch (error) {
      console.error('Error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset Password (localStorage version)
  const handleResetPassword = async (e) => {
    e.preventDefault();

    const newErrors = {};
    ['password', 'password_confirmation'].forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ password: true, password_confirmation: true });
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      const response = await apiService.resetPassword(formData.email, formData.otp.join(''), formData.password, formData.password_confirmation);
      if (!response.success) {
        const errs = response?.data?.errors || {};
        const firstOf = (v) => (Array.isArray(v) ? v[0] : v);
        if (errs.password) {
          setErrors({ password: firstOf(errs.password) });
        } else if (errs.password_confirmation) {
          setErrors({ password_confirmation: firstOf(errs.password_confirmation) });
        } else if (errs.otp || errs.email) {
          // OTP/email problem — send the user back to re-request a code
          setErrors({ general: firstOf(errs.otp) || firstOf(errs.email) || response?.data?.message });
          setStep('otp');
        } else {
          setErrors({ general: response?.data?.message || 'Failed to reset password. Please try again.' });
        }
        setIsLoading(false);
        return;
      }
      // Update user password in localStorage
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      const updatedUsers = users.map(user => {
        if (user.email === formData.email) {
          return { ...user, password: formData.password };
        }
        return user;
      });
      localStorage.setItem('users', JSON.stringify(updatedUsers));

      // Clear reset session
      sessionStorage.removeItem('passwordReset');

      setStep('success');
      setTimeout(() => {
        onClose();
        if (onBackToLogin) onBackToLogin();
      }, 2000);
    } catch (error) {
      console.error('Error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Timer for resend OTP
  const startTimer = () => {
    setCanResend(false);
    setTimer(600);
    const countdown = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = () => {
    if (canResend) {
      handleSendOTP({ preventDefault: () => {} });
    }
  };

  const getPasswordStrengthLabel = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'Weak password';
      case 2:
        return 'Fair password';
      case 3:
        return 'Good password';
      case 4:
        return 'Strong password';
      default:
        return '';
    }
  };

  const getPasswordStrengthClass = () => {
    if (passwordStrength <= 1) return 'weak';
    if (passwordStrength === 2) return 'medium';
    return 'strong';
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto backdrop-blur"
      onClick={handleBackdropClick}
    >
      <div 
        className="w-full max-w-md mx-auto my-4 sm:my-8"
        onClick={handleModalClick}
      >
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 relative">
          {/* Close Button */}
          <button 
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors z-10 w-8 h-8 flex items-center justify-center"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 text-3xl sm:text-4xl">
              🔒
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
              {step === 'email' && 'Forgot Password'}
              {step === 'otp' && 'Verify OTP'}
              {step === 'reset' && 'Reset Password'}
              {step === 'success' && 'Success!'}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 px-2">
              {step === 'email' && 'Enter your email to receive a password reset code'}
              {step === 'otp' && `We sent a code to ${formData.email}`}
              {step === 'reset' && 'Create a new password for your account'}
              {step === 'success' && 'Your password has been reset successfully'}
            </p>
          </div>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center gap-2 text-sm sm:text-base">
              <span>⚠️</span>
              <span className="text-red-600">{errors.general}</span>
            </div>
          )}

          {/* Step 1: Email Input */}
          {step === 'email' && (
            <form onSubmit={handleSendOTP} noValidate>
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="email">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Your email id here"
                    className={`w-full border-2 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base outline-none transition-all ${
                      errors.email && touched.email 
                        ? 'border-red-300 focus:border-red-500' 
                        : 'border-gray-200 focus:border-blue-500'
                    }`}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                {errors.email && touched.email && (
                  <div className="flex items-center gap-2 mt-2 text-xs sm:text-sm text-red-600">
                    <span>⚠️</span>
                    <span>{errors.email}</span>
                  </div>
                )}
              </div>

              <button 
                type="submit" 
                className="w-full bg-blue-600 text-white py-3 sm:py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="loading-spinner"></span>
                    Sending...
                  </span>
                ) : (
                  'Send Reset Code'
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} noValidate>
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3 sm:mb-4 text-center">
                  Enter 6-digit code
                </label>
                <div className="flex gap-2 sm:gap-3 justify-center mb-3 sm:mb-4">
                  {formData.otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      id={`otp-${index}`}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 rounded-xl outline-none transition-all ${
                        errors.otp ? 'border-red-300' : 'border-gray-300 focus:border-blue-500'
                      }`}
                      maxLength={1}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
                {errors.otp && (
                  <div className="flex items-center gap-2 justify-center text-xs sm:text-sm text-red-600">
                    <span>⚠️</span>
                    <span>{errors.otp}</span>
                  </div>
                )}
                <div className="text-center mt-3 sm:mt-4">
                  {canResend ? (
                    <button 
                      type="button" 
                      onClick={handleResendOTP} 
                      className="text-blue-600 font-semibold hover:underline text-sm sm:text-base"
                    >
                      Resend Code
                    </button>
                  ) : (
                    <span className="text-gray-500 text-sm sm:text-base">
                      Resend code in <span className="font-semibold text-blue-600">{timer}s</span>
                    </span>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                className="w-full bg-blue-600 text-white py-3 sm:py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="loading-spinner"></span>
                    Verifying...
                  </span>
                ) : (
                  'Verify Code'
                )}
              </button>
            </form>
          )}

          {/* Step 3: Reset Password */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} noValidate>
              <div className="space-y-4 sm:space-y-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="password">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Create a strong password"
                      className={`w-full border-2 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base outline-none transition-all ${
                        errors.password && touched.password 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-200 focus:border-blue-500'
                      }`}
                      autoComplete="new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg sm:text-xl"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️‍🗨️' : '👁️'}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <div className="flex items-center gap-2 mt-2 text-xs sm:text-sm text-red-600">
                      <span>⚠️</span>
                      <span>{errors.password}</span>
                    </div>
                  )}
                  {formData.password && (
                    <div className="mt-2 sm:mt-3">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded-full transition-all ${
                              level <= passwordStrength
                                ? passwordStrength <= 1
                                  ? 'bg-red-500'
                                  : passwordStrength === 2
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="text-xs text-gray-600">
                        Password strength: <span className="font-semibold">{getPasswordStrengthLabel()}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="password_confirmation">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="password_confirmation"
                      name="password_confirmation"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Re-enter your password"
                      className={`w-full border-2 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base outline-none transition-all ${
                        errors.password_confirmation && touched.password_confirmation 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-200 focus:border-blue-500'
                      }`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg sm:text-xl"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
                    </button>
                  </div>
                  {errors.password_confirmation && touched.password_confirmation && (
                    <div className="flex items-center gap-2 mt-2 text-xs sm:text-sm text-red-600">
                      <span>⚠️</span>
                      <span>{errors.password_confirmation}</span>
                    </div>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                className="w-full bg-blue-600 text-white py-3 sm:py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="loading-spinner"></span>
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center py-4 sm:py-8">
              <div className="text-5xl sm:text-6xl mb-4">✅</div>
              <p className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Your password has been reset successfully!
              </p>
              <p className="text-sm sm:text-base text-gray-600">
                You can now login with your new password
              </p>
            </div>
          )}

          {/* Footer */}
          {step !== 'success' && (
            <div className="text-center mt-6 sm:mt-8 text-sm sm:text-base text-gray-600">
              Remember your password?{' '}
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (onBackToLogin) onBackToLogin();
                  onClose();
                }}
                className="text-blue-600 hover:underline font-semibold"
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;